import React, { useEffect } from "react";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { SingleValue } from "react-select";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../api/useCategories";

export const useSystemsInput = (selectedEntry: { systemId: string | null }) => {
  const systems = useCategories("SYSTEM");
  const createSystem = useCreateCategory("SYSTEM");
  const updateSystem = useUpdateCategory("SYSTEM");
  const deleteSystem = useDeleteCategory("SYSTEM");
  const [dirty, setDirty] = React.useState(false);

  const systemDefaultValue = React.useMemo(
    () =>
      systems
        .filter(id => selectedEntry?.systemId === id)
        .map(id => ({ value: id, label: id }))[0] ?? null,
    [selectedEntry?.systemId, systems],
  );

  const [selectedSystem, setSelectedSystem] =
    React.useState<SingleValue<BaseOption>>(systemDefaultValue);

  const systemOptions = React.useMemo(
    () => buildOptions([...systems.map(e => ({ label: e, value: e }))]),
    [systems],
  );

  useEffect(() => {
    if (selectedSystem?.value && !systems.includes(selectedSystem.value)) {
      setSelectedSystem(null);
    }
  }, [selectedSystem, systems]);

  const handleSystemChanged = React.useCallback(
    (value: SingleValue<BaseOption>) => {
      setSelectedSystem(value);
      setDirty(value !== systemDefaultValue);
    },
    [systemDefaultValue],
  );

  const handleSystemCreated = React.useCallback(
    async (newSystem: string) => {
      await createSystem(newSystem);
      setSelectedSystem({ value: newSystem, label: newSystem });
      setDirty(true);
    },
    [createSystem],
  );

  const handleSystemDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        if (selectedSystem?.value === option.value) setSelectedSystem(null);
        await deleteSystem(option.value);
        setDirty(true);
      }
    },
    [deleteSystem, selectedSystem?.value],
  );

  const handleSystemEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        await updateSystem(value, label);
        setSelectedSystem({ label, value: label });
        setDirty(true);
      }
    },
    [updateSystem],
  );

  const resetValues = React.useCallback(() => {
    setSelectedSystem(systemDefaultValue);
    setDirty(false);
  }, [systemDefaultValue]);

  return {
    systems,
    systemOptions,
    systemDefaultValue,
    selectedSystem,
    dirty,
    resetValues,
    handleSystemChanged,
    handleSystemCreated,
    handleSystemDeleted,
    handleSystemEdited,
  };
};
